import * as React from "react";
import type { DashboardSummary } from "@excellent/core/server";

import { ProductDashboard } from "./product-dashboard";
import type { DashboardRangePreset } from "./product-dashboard-header";
import { remapDashboardSummary } from "./remap-summary";

export interface SectionProductDashboardProps {
  /** Product slug, e.g. "crm" — used for region keys/data-attr, not lookup. */
  productName: string;
  /** Display label, e.g. "Sales". */
  label: string;
  /** Section base path, e.g. `/projects/<slug>/sales`. */
  basePath: string;
  /**
   * The dashboard's own path, used as the base for range-pill navigation.
   * Defaults to `${basePath}/overview`; pass e.g. `${basePath}/docs` or
   * `${basePath}/lens` for sections that mount the dashboard under a
   * sub-namespace.
   */
  rangeBasePath?: string;
  /** Pre-computed payload from `productDashboardPage(...)`. */
  summary: DashboardSummary;
  /** One-line tagline from the product manifest. */
  tagline?: string;
  /** Date-range pill row appears when true. */
  timeScoped?: boolean;
  /** Current preset for the range pills. */
  currentRange?: DashboardRangePreset;
  /** Header strip action area (e.g. "New lead"). */
  headerActions?: React.ReactNode;
  /** Single primary header action (alternative to `headerActions`). */
  primaryAction?: { label: string; href: string };
  /** Domain-specific activity empty-state body. */
  activityEmptyBody?: string;
  /**
   * Rewrites a product href (`/crm/leads`) to the in-section path. Build with
   * {@link makeSectionHrefRemapper}; the basePath is applied for you.
   */
  remapHref: (href: string, basePath: string) => string;
}

/**
 * Renders a product dashboard inside a project section. Was the same ~45 lines
 * of href-remap + 5-region layout hand-copied into every
 * `_<product>/dashboard-view.tsx` (crm/hiring/document/lens). Now: remap the
 * summary once, then delegate to the shared {@link ProductDashboard}
 * orchestrator in embedded (no page-padding) mode so the section and the
 * standalone product render identically.
 */
export function SectionProductDashboard({
  productName,
  label,
  basePath,
  rangeBasePath,
  summary,
  tagline,
  timeScoped,
  currentRange,
  headerActions,
  primaryAction,
  activityEmptyBody,
  remapHref,
}: SectionProductDashboardProps) {
  const remapped = remapDashboardSummary(summary, (h) => remapHref(h, basePath));
  return (
    <ProductDashboard
      productName={productName}
      label={label}
      tagline={tagline}
      basePath={rangeBasePath ?? `${basePath}/overview`}
      timeScoped={timeScoped}
      currentRange={currentRange}
      headerActions={headerActions}
      primaryAction={primaryAction}
      activityEmptyBody={activityEmptyBody}
      summary={remapped}
      embedded
    />
  );
}
